import { useEffect } from "react";

/**
 * Hook for infinite scroll pagination triggered only on vertical scroll to bottom.
 *
 * @param {string} containerId - The id of the scroll container.
 * @param {boolean} isFetching - Is data currently being fetched.
 * @param {boolean} isNextPage - Is there more data to fetch.
 * @param {number} paginationLimit - Items to fetch per page.
 * @param {(limit: number) => void} onPaginate - Function to call to increment offset.
 */
export const useVerticalScroll = ({
  containerId,
  isFetching,
  isNextPage,
  paginationLimit,
  onPaginate,
}) => {
  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    let lastScrollTop = container.scrollTop;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      const isVerticalScroll = scrollTop !== lastScrollTop;
      lastScrollTop = scrollTop;

      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 1;

      if (isVerticalScroll && scrolledToBottom && !isFetching && isNextPage) {
        onPaginate(paginationLimit);
      }
    };

    container.addEventListener("scroll", onScroll);

    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [containerId, isFetching, isNextPage, paginationLimit, onPaginate]);
};
